import classcat from "classcat";
import RcSlider from "rc-slider";
import "rc-slider/assets/index.css";
import React from "react";

import { PriceRange } from "@is/types";

import css from "./PriceRangeSlider.module.css";
import useManagePriceRangeSlider from "./hooks/useManagePriceRangeSlider";

type Props = {
  minValue: number;
  maxValue: number;
  value: PriceRange;
  currencyCode: string;
  onChange: (range: PriceRange) => void;
  onRelease?: (range: PriceRange) => void;
  className?: string;
};

// rc-slider drives the track/handles in a linear 0–100 position space (no
// handle "pushing", proper touch handling); the hook maps that to/from
// logarithmic prices. allowCross={false} keeps the handles ordered without
// shoving each other.
const PriceRangeSlider = ({
  minValue,
  maxValue,
  value,
  currencyCode,
  onChange,
  onRelease,
  className,
}: Props) => {
  const {
    sliderValue,
    sliderMin,
    sliderMax,
    sliderStep,
    handleSliderChange,
    handleSliderComplete,
    localMin,
    localMax,
    minLabelPercent,
    maxLabelPercent,
    minLabelTranslateX,
    maxLabelTranslateX,
    sliderContainerRef,
    formatMinPrice,
    formatMaxPrice,
  } = useManagePriceRangeSlider({
    minValue,
    maxValue,
    value,
    onChange,
    onRelease,
  });

  const currencySuffix = currencyCode ? ` ${currencyCode}` : "";

  return (
    <div className={classcat([css.container, className])}>
      <div ref={sliderContainerRef} className={css.sliderWrapper}>
        <RcSlider
          range
          allowCross={false}
          min={sliderMin}
          max={sliderMax}
          step={sliderStep}
          value={sliderValue}
          onChange={handleSliderChange}
          onChangeComplete={handleSliderComplete}
          ariaLabelForHandle={["Minimum price", "Maximum price"]}
        />
      </div>

      <div className={css.valuesContainer}>
        <span
          className={classcat(["text-label-lg", css.valueDisplay])}
          style={{
            left: `${minLabelPercent}%`,
            transform: `translateX(${minLabelTranslateX}%)`,
          }}
        >
          {formatMinPrice(localMin)}
          {currencySuffix}
        </span>
        <span
          className={classcat(["text-label-lg", css.valueDisplay])}
          style={{
            left: `${maxLabelPercent}%`,
            transform: `translateX(${maxLabelTranslateX}%)`,
          }}
        >
          {formatMaxPrice(localMax)}
          {currencySuffix}
        </span>
      </div>
    </div>
  );
};

export default PriceRangeSlider;
